import { useState } from 'react';
import type { ProfileSelections } from '@/types/consumer';
import {
  AGE_RANGES,
  AGE_RANGES_ES,
  INCOME_LEVELS,
  INCOME_LEVELS_ES,
  LOCATION_TYPES,
  LOCATION_TYPES_ES,
  GENDERS,
  GENDERS_ES,
  REGIONS,
  REGIONS_ES,
  HOUSEHOLD_SIZES,
  HOUSEHOLD_SIZES_ES,
  HAS_CHILDREN,
  HAS_CHILDREN_ES,
  EDUCATION_LEVELS,
  EDUCATION_LEVELS_ES,
  OCCUPATIONS,
  OCCUPATIONS_ES,
  MARITAL_STATUSES,
  MARITAL_STATUSES_ES,
  LIFESTYLE_SEGMENTS,
  LIFESTYLE_SEGMENTS_ES,
  CONSUMPTION_FREQUENCIES,
  CONSUMPTION_FREQUENCIES_ES,
  CHANNELS,
  CHANNELS_ES,
} from '@/types/consumer';

interface ProfileBuilderProps {
  side: 'A' | 'B';
  brands: Array<{ name: string; share: number }>;
  onGenerate: (selections: ProfileSelections) => void;
  language: string;
  className?: string;
}

export function ProfileBuilder({
  side,
  brands,
  onGenerate,
  language,
  className = '',
}: ProfileBuilderProps) {
  const t = language === 'es';

  const [selections, setSelections] = useState<ProfileSelections>({
    // Demographics (multi-select where applicable)
    ageRanges: AGE_RANGES.map(a => a.value), // All selected by default
    gender: 'all',
    incomeLevel: 'all',
    locationTypes: LOCATION_TYPES.map(l => l.value), // All selected by default
    region: 'national',
    householdSize: 'all',
    hasChildren: 'all',
    educationLevel: 'all',
    occupation: 'all',
    maritalStatus: 'all',
    lifestyleSegment: 'all',
    // Soda consumption
    consumptionFrequency: 'all',
    brandPreference: brands[0]?.name || '',
    // Channel
    channelPreference: 'all',
  });

  // Get localized options
  const ageRanges = t ? AGE_RANGES_ES : AGE_RANGES;
  const incomeLevels = t ? INCOME_LEVELS_ES : INCOME_LEVELS;
  const locationTypes = t ? LOCATION_TYPES_ES : LOCATION_TYPES;
  const genders = t ? GENDERS_ES : GENDERS;
  const regions = t ? REGIONS_ES : REGIONS;
  const householdSizes = t ? HOUSEHOLD_SIZES_ES : HOUSEHOLD_SIZES;
  const hasChildrenOptions = t ? HAS_CHILDREN_ES : HAS_CHILDREN;
  const educationLevels = t ? EDUCATION_LEVELS_ES : EDUCATION_LEVELS;
  const occupations = t ? OCCUPATIONS_ES : OCCUPATIONS;
  const maritalStatuses = t ? MARITAL_STATUSES_ES : MARITAL_STATUSES;
  const lifestyleSegments = t ? LIFESTYLE_SEGMENTS_ES : LIFESTYLE_SEGMENTS;
  const consumptionFrequencies = t ? CONSUMPTION_FREQUENCIES_ES : CONSUMPTION_FREQUENCIES;
  const channels = t ? CHANNELS_ES : CHANNELS;

  const handleChange = (field: keyof ProfileSelections, value: string | boolean) => {
    setSelections(prev => ({ ...prev, [field]: value }));
  };

  // Toggle multi-select for age ranges
  const toggleAgeRange = (value: string) => {
    setSelections(prev => {
      const current = prev.ageRanges;
      if (current.includes(value)) {
        // Don't allow deselecting all
        if (current.length === 1) return prev;
        return { ...prev, ageRanges: current.filter(v => v !== value) };
      }
      return { ...prev, ageRanges: [...current, value] };
    });
  };

  // Toggle multi-select for location types
  const toggleLocationType = (value: string) => {
    setSelections(prev => {
      const current = prev.locationTypes;
      if (current.includes(value)) {
        // Don't allow deselecting all
        if (current.length === 1) return prev;
        return { ...prev, locationTypes: current.filter(v => v !== value) };
      }
      return { ...prev, locationTypes: [...current, value] };
    });
  };

  // Select all age ranges
  const selectAllAgeRanges = () => {
    setSelections(prev => ({
      ...prev,
      ageRanges: AGE_RANGES.map(a => a.value),
    }));
  };

  // Select all location types
  const selectAllLocationTypes = () => {
    setSelections(prev => ({
      ...prev,
      locationTypes: LOCATION_TYPES.map(l => l.value),
    }));
  };

  const handleGenerate = () => {
    onGenerate(selections);
  };

  return (
    <div className={`card p-5 ${className}`}>
      <h3 className="text-lg font-semibold text-white mb-4">
        {t ? `Perfil ${side}` : `Profile ${side}`}
      </h3>

      <div className="space-y-4">
        {/* === DEMOGRAPHICS SECTION === */}
        <div className="text-xs text-text-tertiary uppercase tracking-wider mb-2">
          {t ? 'Variables Demográficas' : 'Demographic Variables'}
        </div>

        {/* Age Ranges - Multi-select */}
        <div>
          <div className="flex justify-between items-center mb-1.5">
            <label className="text-sm text-text-secondary">
              {t ? 'Rango de Edad' : 'Age Range'}
            </label>
            <button
              onClick={selectAllAgeRanges}
              className="text-xs text-primary-400 hover:text-primary-300"
            >
              {t ? 'Seleccionar todos' : 'Select all'}
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {ageRanges.map(opt => (
              <button
                key={opt.value}
                onClick={() => toggleAgeRange(opt.value)}
                className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                  selections.ageRanges.includes(opt.value)
                    ? 'bg-primary-500/20 border-primary-500 text-primary-300'
                    : 'bg-surface-700 border-surface-600 text-text-secondary hover:border-surface-500'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Gender */}
        <div>
          <label className="block text-sm text-text-secondary mb-1.5">
            {t ? 'Género' : 'Gender'}
          </label>
          <div className="flex flex-wrap gap-2">
            {genders.map(opt => (
              <button
                key={opt.value}
                onClick={() => handleChange('gender', opt.value)}
                className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                  selections.gender === opt.value
                    ? 'bg-primary-500/20 border-primary-500 text-primary-300'
                    : 'bg-surface-700 border-surface-600 text-text-secondary hover:border-surface-500'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Income Level (NSE) */}
        <div>
          <label className="block text-sm text-text-secondary mb-1.5">
            {t ? 'Nivel Socioeconómico (NSE)' : 'Socioeconomic Level (NSE)'}
          </label>
          <select
            value={selections.incomeLevel}
            onChange={e => handleChange('incomeLevel', e.target.value)}
            className="w-full px-3 py-2 bg-surface-700 border border-surface-600 rounded-lg text-white text-sm focus:outline-none focus:border-primary-500"
          >
            {incomeLevels.map(opt => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* Location Types - Multi-select */}
        <div>
          <div className="flex justify-between items-center mb-1.5">
            <label className="text-sm text-text-secondary">
              {t ? 'Tipo de Ubicación' : 'Location Type'}
            </label>
            <button
              onClick={selectAllLocationTypes}
              className="text-xs text-primary-400 hover:text-primary-300"
            >
              {t ? 'Seleccionar todos' : 'Select all'}
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {locationTypes.map(opt => (
              <button
                key={opt.value}
                onClick={() => toggleLocationType(opt.value)}
                className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                  selections.locationTypes.includes(opt.value)
                    ? 'bg-primary-500/20 border-primary-500 text-primary-300'
                    : 'bg-surface-700 border-surface-600 text-text-secondary hover:border-surface-500'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Region */}
        <div>
          <label className="block text-sm text-text-secondary mb-1.5">
            {t ? 'Región' : 'Region'}
          </label>
          <select
            value={selections.region}
            onChange={e => handleChange('region', e.target.value)}
            className="w-full px-3 py-2 bg-surface-700 border border-surface-600 rounded-lg text-white text-sm focus:outline-none focus:border-primary-500"
          >
            {regions.map(opt => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* Household Size & Children - Same Row */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm text-text-secondary mb-1.5">
              {t ? 'Tamaño del Hogar' : 'Household Size'}
            </label>
            <select
              value={selections.householdSize}
              onChange={e => handleChange('householdSize', e.target.value)}
              className="w-full px-3 py-2 bg-surface-700 border border-surface-600 rounded-lg text-white text-sm focus:outline-none focus:border-primary-500"
            >
              {householdSizes.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm text-text-secondary mb-1.5">
              {t ? 'Niños en Hogar' : 'Children'}
            </label>
            <select
              value={selections.hasChildren}
              onChange={e => handleChange('hasChildren', e.target.value)}
              className="w-full px-3 py-2 bg-surface-700 border border-surface-600 rounded-lg text-white text-sm focus:outline-none focus:border-primary-500"
            >
              {hasChildrenOptions.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Education & Occupation - Same Row */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm text-text-secondary mb-1.5">
              {t ? 'Educación' : 'Education'}
            </label>
            <select
              value={selections.educationLevel}
              onChange={e => handleChange('educationLevel', e.target.value)}
              className="w-full px-3 py-2 bg-surface-700 border border-surface-600 rounded-lg text-white text-sm focus:outline-none focus:border-primary-500"
            >
              {educationLevels.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm text-text-secondary mb-1.5">
              {t ? 'Ocupación' : 'Occupation'}
            </label>
            <select
              value={selections.occupation}
              onChange={e => handleChange('occupation', e.target.value)}
              className="w-full px-3 py-2 bg-surface-700 border border-surface-600 rounded-lg text-white text-sm focus:outline-none focus:border-primary-500"
            >
              {occupations.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Marital Status & Lifestyle - Same Row */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm text-text-secondary mb-1.5">
              {t ? 'Estado Civil' : 'Marital Status'}
            </label>
            <select
              value={selections.maritalStatus}
              onChange={e => handleChange('maritalStatus', e.target.value)}
              className="w-full px-3 py-2 bg-surface-700 border border-surface-600 rounded-lg text-white text-sm focus:outline-none focus:border-primary-500"
            >
              {maritalStatuses.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm text-text-secondary mb-1.5">
              {t ? 'Estilo de Vida' : 'Lifestyle'}
            </label>
            <select
              value={selections.lifestyleSegment}
              onChange={e => handleChange('lifestyleSegment', e.target.value)}
              className="w-full px-3 py-2 bg-surface-700 border border-surface-600 rounded-lg text-white text-sm focus:outline-none focus:border-primary-500"
            >
              {lifestyleSegments.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* === SODA CONSUMPTION SECTION === */}
        <div className="text-xs text-text-tertiary uppercase tracking-wider mt-4 mb-2 pt-4 border-t border-surface-700">
          {t ? 'Consumo de Refresco' : 'Soda Consumption'}
        </div>

        {/* Consumption Frequency & Brand - Same Row */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm text-text-secondary mb-1.5">
              {t ? 'Frecuencia' : 'Frequency'}
            </label>
            <select
              value={selections.consumptionFrequency}
              onChange={e => handleChange('consumptionFrequency', e.target.value)}
              className="w-full px-3 py-2 bg-surface-700 border border-surface-600 rounded-lg text-white text-sm focus:outline-none focus:border-primary-500"
            >
              {consumptionFrequencies.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm text-text-secondary mb-1.5">
              {t ? 'Marca Preferida' : 'Brand Preference'}
            </label>
            <select
              value={selections.brandPreference}
              onChange={e => handleChange('brandPreference', e.target.value)}
              className="w-full px-3 py-2 bg-surface-700 border border-surface-600 rounded-lg text-white text-sm focus:outline-none focus:border-primary-500"
            >
              <option value="">{t ? 'Cualquiera' : 'Any'}</option>
              {brands.map(brand => (
                <option key={brand.name} value={brand.name}>
                  {brand.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* === CHANNEL SECTION === */}
        <div className="text-xs text-text-tertiary uppercase tracking-wider mt-4 mb-2 pt-4 border-t border-surface-700">
          {t ? 'Canal de Compra' : 'Purchase Channel'}
        </div>

        <div>
          <label className="block text-sm text-text-secondary mb-1.5">
            {t ? 'Canal Preferido' : 'Preferred Channel'}
          </label>
          <select
            value={selections.channelPreference}
            onChange={e => handleChange('channelPreference', e.target.value)}
            className="w-full px-3 py-2 bg-surface-700 border border-surface-600 rounded-lg text-white text-sm focus:outline-none focus:border-primary-500"
          >
            {channels.map(opt => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* Generate Button */}
        <button
          onClick={handleGenerate}
          className="w-full mt-4 px-4 py-2.5 bg-primary-500 hover:bg-primary-400 text-white font-medium rounded-lg transition-colors"
        >
          {t ? 'Generar Perfil' : 'Generate Profile'}
        </button>
      </div>
    </div>
  );
}
